import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaFilter, FaDownload, FaFileExcel, FaUsers, FaTag } from 'react-icons/fa';
import { MdSchool, MdPeople, MdTrendingUp, MdBusiness } from 'react-icons/md';
import { supabase } from '../services/supabaseClient'; // Asegúrate de que la ruta sea correcta

const CourseAdmin = ({ user }) => {
    const [courses, setCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [submitting, setSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [showRequestsModal, setShowRequestsModal] = useState(false);
    const [selectedCourseRequests, setSelectedCourseRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        duration: '',
        modality: 'Virtual',
        regular_price: '',
        discount_percentage: 0,
        discounted_price: '',
        discount_deadline: '',
        company_name: '',
        start_date: '',
        end_date: '',
        instructor: '',
        max_students: '',
        current_students: 0,
        requirements: '',
        objectives: '',
        syllabus: '',
        image_url: '',
        status: 'available'
    });
    const [formErrors, setFormErrors] = useState({});
// Función para cargar cursos desde Supabase
const loadCourses = async () => {
    try {
        setLoading(true);
        
        const { data: coursesData, error: coursesError } = await supabase
            .from('courses')
            .select('*')
            .order('created_at', { ascending: false });

        if (coursesError) {
            throw coursesError;
        }

        // Cargar el contador real de solicitudes para cada curso
        const processedCourses = await Promise.all(coursesData.map(async (course) => {
            // Contar solicitudes reales para este curso
            const { count, error: countError } = await supabase
                .from('course_requests')
                .select('*', { count: 'exact', head: true })
                .eq('course_id', course.id);

            if (countError) {
                console.warn('Error counting requests for course:', course.id, countError);
            }

            return {
                ...course,
                requestCount: count || 0, // Usar el conteo real
                // Asegurar que los precios sean números
                regular_price: parseFloat(course.regular_price) || 0,
                discounted_price: parseFloat(course.discounted_price) || 0,
                discount_percentage: parseFloat(course.discount_percentage) || 0,
                current_students: parseInt(course.current_students) || 0,
                max_students: parseInt(course.max_students) || 0
            };
        }));

        setCourses(processedCourses);
        
    } catch (error) {
        console.error('Error loading courses:', error);
        alert(`❌ Error al cargar cursos: ${error.message}`);
        setCourses([]);
    } finally {
        setLoading(false);
    }
};

    // Cargar cursos al montar el componente
    useEffect(() => {
        loadCourses();
    }, []);

    useEffect(() => {
        filterCourses();
    }, [courses, searchTerm, filterStatus, filterCategory]);

    // Calcular precio con descuento automáticamente
    useEffect(() => {
        if (formData.regular_price && formData.discount_percentage) {
            const regularPrice = parseFloat(formData.regular_price);
            const discountPercent = parseFloat(formData.discount_percentage);
            if (!isNaN(regularPrice) && !isNaN(discountPercent)) {
                const discountedPrice = regularPrice - (regularPrice * discountPercent / 100);
                setFormData(prev => ({
                    ...prev,
                    discounted_price: discountedPrice.toFixed(2)
                }));
            }
        } else if (formData.regular_price && (!formData.discount_percentage || formData.discount_percentage == 0)) {
            // Si no hay descuento, el precio con descuento es igual al precio regular
            setFormData(prev => ({
                ...prev,
                discounted_price: formData.regular_price
            }));
        }
    }, [formData.regular_price, formData.discount_percentage]);
// Agregar useEffect para refresh automático cada 30 segundos
useEffect(() => {
    let interval;
    if (showRequestsModal) {
        interval = setInterval(() => {
            if (selectedCourseRequests.course) {
                handleViewRequests(selectedCourseRequests.course);
            }
        }, 30000); // 30 segundos
    }
    
    return () => {
        if (interval) {
            clearInterval(interval);
        }
    };
}, [showRequestsModal, selectedCourseRequests.course]);
// Agregar después de loadCourses
const refreshData = async () => {
    await loadCourses();
    if (showRequestsModal && selectedCourseRequests.course) {
        await handleViewRequests(selectedCourseRequests.course);
    }
};
    const filterCourses = () => {
        let filtered = [...courses];

        if (searchTerm) {
            filtered = filtered.filter(course =>
                course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.category.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterStatus !== 'all') {
            filtered = filtered.filter(course => course.status === filterStatus);
        }

        if (filterCategory !== 'all') {
            filtered = filtered.filter(course => course.category === filterCategory);
        }

        setFilteredCourses(filtered);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) errors.title = 'El título es requerido';
        if (!formData.description.trim()) errors.description = 'La descripción es requerida';
        if (!formData.category) errors.category = 'La categoría es requerida';
        if (!formData.duration.trim()) errors.duration = 'La duración es requerida';
        if (!formData.regular_price || isNaN(formData.regular_price)) errors.regular_price = 'El precio regular debe ser un número válido';
        if (!formData.company_name.trim()) errors.company_name = 'El nombre de la empresa es requerido';
        if (!formData.start_date) errors.start_date = 'La fecha de inicio es requerida';
        if (!formData.end_date) errors.end_date = 'La fecha de fin es requerida';
        if (!formData.instructor.trim()) errors.instructor = 'El instructor es requerido';
        if (!formData.max_students || isNaN(formData.max_students)) errors.max_students = 'El número máximo de estudiantes debe ser válido';

        if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
            errors.end_date = 'La fecha de fin debe ser posterior a la fecha de inicio';
        }

        if (formData.discount_percentage && formData.discount_percentage > 0 && !formData.discount_deadline) {
            errors.discount_deadline = 'La fecha límite del descuento es requerida cuando hay descuento';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);
            
            // Preparar datos para Supabase
            const courseData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                category: formData.category,
                duration: formData.duration.trim(),
                modality: formData.modality,
                regular_price: parseFloat(formData.regular_price),
                discount_percentage: parseFloat(formData.discount_percentage) || 0,
                discounted_price: parseFloat(formData.discounted_price) || parseFloat(formData.regular_price),
                discount_deadline: formData.discount_deadline || null,
                company_name: formData.company_name.trim(),
                start_date: formData.start_date,
                end_date: formData.end_date,
                instructor: formData.instructor.trim(),
                max_students: parseInt(formData.max_students),
                current_students: parseInt(formData.current_students) || 0,
                requirements: formData.requirements.trim() || null,
                objectives: formData.objectives.trim() || null,
                syllabus: formData.syllabus.trim() || null,
                image_url: formData.image_url.trim() || null,
                status: formData.status
            };

            let result;
            
            if (editingCourse) {
                // Actualizar curso existente
                result = await supabase
                    .from('courses')
                    .update(courseData)
                    .eq('id', editingCourse.id)
                    .select();
            } else {
                // Crear nuevo curso
                result = await supabase
                    .from('courses')
                    .insert([courseData])
                    .select();
            }

            if (result.error) {
                throw result.error;
            }
            
            const action = editingCourse ? 'actualizado' : 'creado';
            alert(`✅ Curso ${action} exitosamente!`);
            
            // Recargar la lista de cursos
            await loadCourses();
            
            resetForm();
            setShowModal(false);
            
        } catch (error) {
            console.error('Error saving course:', error);
            alert(`❌ Error al guardar el curso: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            category: '',
            duration: '',
            modality: 'Virtual',
            regular_price: '',
            discount_percentage: 0,
            discounted_price: '',
            discount_deadline: '',
            company_name: '',
            start_date: '',
            end_date: '',
            instructor: '',
            max_students: '',
            current_students: 0,
            requirements: '',
            objectives: '',
            syllabus: '',
            image_url: '',
            status: 'available'
        });
        setFormErrors({});
        setEditingCourse(null);
    };

    const handleEdit = (course) => {
        setEditingCourse(course);
        setFormData({
            title: course.title,
            description: course.description,
            category: course.category,
            duration: course.duration,
            modality: course.modality,
            regular_price: course.regular_price.toString(),
            discount_percentage: course.discount_percentage,
            discounted_price: course.discounted_price.toString(),
            discount_deadline: course.discount_deadline || '',
            company_name: course.company_name,
            start_date: course.start_date,
            end_date: course.end_date,
            instructor: course.instructor,
            max_students: course.max_students.toString(),
            current_students: course.current_students,
            requirements: course.requirements || '',
            objectives: course.objectives || '',
            syllabus: course.syllabus || '',
            image_url: course.image_url || '',
            status: course.status
        });
        setShowModal(true);
    };

    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            const { error } = await supabase
                .from('courses')
                .delete()
                .eq('id', courseToDelete.id);

            if (error) {
                throw error;
            }

            await loadCourses();
            setShowDeleteModal(false);
            setCourseToDelete(null);
            alert('✅ Curso eliminado exitosamente!');
            
        } catch (error) {
            console.error('Error deleting course:', error);
            alert(`❌ Error al eliminar el curso: ${error.message}`);
        }
    };
const updateRequestStatus = async (requestId, newStatus) => {
    // Confirmación para estados críticos
    if (newStatus === 'cancelled') {
        const confirm = window.confirm('¿Estás seguro de que deseas cancelar esta solicitud?');
        if (!confirm) return;
    }

    try {
        const { error } = await supabase
            .from('course_requests')
            .update({ status: newStatus })
            .eq('id', requestId);

        if (error) {
            throw error;
        }

        // Actualizar el estado local sin recargar todo
        setSelectedCourseRequests(prev => ({
            ...prev,
            requests: prev.requests.map(request => 
                request.id === requestId 
                    ? { ...request, status: newStatus }
                    : request
            )
        }));

        // También actualizar la lista de cursos para refrescar contadores si es necesario
        await loadCourses();

        // Mostrar mensaje de éxito
        const statusText = {
            'pending': 'Pendiente',
            'contacted': 'Contactado',
            'enrolled': 'Matriculado',
            'cancelled': 'Cancelado',
            'completed': 'Completado'
        };
        
        alert(`✅ Estado actualizado a: ${statusText[newStatus]}`);

    } catch (error) {
        console.error('Error updating request status:', error);
        alert(`❌ Error al actualizar el estado: ${error.message}`);
    }
};
const handleViewRequests = async (course) => {
    try {
        setLoadingRequests(true);
        setShowRequestsModal(true);
        
        // Cargar solicitudes reales desde Supabase
        const { data, error } = await supabase
            .from('course_requests')
            .select('*')
            .eq('course_id', course.id)
            .order('created_at', { ascending: false });

        if (error) {
            throw error;
        }

        // Procesar los datos para mostrar en el formato correcto
        const processedRequests = data.map(request => ({
            id: request.id,
            name: request.name,
            email: request.email,
            company: request.company || 'No especificada', // Campo opcional
            ruc: request.ruc || 'No especificado', // Campo opcional
            phone: request.phone,
            request_date: request.request_date || request.created_at, // Usar created_at como fallback
            status: request.status,
            created_at: request.created_at
        }));

        setSelectedCourseRequests({
            course: course,
            requests: processedRequests
        });
        
    } catch (error) {
        console.error('Error loading requests:', error);
        alert(`Error al cargar las solicitudes: ${error.message}`);
        setSelectedCourseRequests({
            course: course,
            requests: []
        });
    } finally {
        setLoadingRequests(false);
    }
};
    const getStatusBadge = (status) => {
        const statusConfig = {
            available: { class: 'bg-success', text: 'Disponible' },
            coming_soon: { class: 'bg-warning', text: 'Próximamente' },
            closed: { class: 'bg-secondary', text: 'Cerrado' },
            full: { class: 'bg-danger', text: 'Completo' }
        };
        
        const config = statusConfig[status] || statusConfig.available;
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    const categories = ['Contratación', 'Legal', 'Gestión', 'Finanzas', 'Administración'];

    // Verificación de permisos
    if (user?.plan !== 'ADMI') {
        return (
            <div className="container-fluid py-4">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="alert alert-warning text-center">
                            <h4><MdSchool className="me-2" />Acceso Denegado</h4>
                            <p className="mb-0">No tienes permisos para acceder a la gestión de cursos.</p>
                            <small className="text-muted">Solo los administradores pueden gestionar cursos.</small>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        <p className="text-muted">Cargando cursos desde la base de datos...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    {/* Header con estadísticas */}
                    <div className="row mb-4">
                        <div className="col-md-8">
                            <h1 className="h3 mb-1">
                                <MdSchool className="me-2 text-info" />
                                Gestión de Cursos
                            </h1>
                            <p className="text-muted">Administra todos los cursos disponibles en la plataforma</p>
                        </div>
                        <div className="col-md-4 text-md-end">
                            <button 
                                className="btn btn-primary me-2"
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                            >
                                <FaPlus className="me-2" />
                                Nuevo Curso
                            </button>
                            <button 
                                className="btn btn-outline-success"
                                title="Exportar a CSV"
                            >
                                <FaFileExcel />
                            </button>
                        </div>
                    </div>

                    {/* Cards de estadísticas */}
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="card bg-primary text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h4 className="card-title">{courses.length}</h4>
                                            <p className="card-text">Total Cursos</p>
                                        </div>
                                        <div className="align-self-center">
                                            <MdSchool size={40} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-success text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h4 className="card-title">{courses.filter(c => c.status === 'available').length}</h4>
                                            <p className="card-text">Disponibles</p>
                                        </div>
                                        <div className="align-self-center">
                                            <MdTrendingUp size={40} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-info text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h4 className="card-title">
                                                {courses.reduce((total, course) => total + (course.current_students || 0), 0)}
                                            </h4>
                                            <p className="card-text">Estudiantes</p>
                                        </div>
                                        <div className="align-self-center">
                                            <MdPeople size={40} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-warning text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h4 className="card-title">
                                                {courses.reduce((total, course) => total + (course.requestCount || 0), 0)}
                                            </h4>
                                            <p className="card-text">Solicitudes</p>
                                        </div>
                                        <div className="align-self-center">
                                            <FaEye size={40} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filtros y búsqueda */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaSearch />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar cursos..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={filterCategory}
                                        onChange={(e) => setFilterCategory(e.target.value)}
                                    >
                                        <option value="all">Todas las categorías</option>
                                        {categories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="all">Todos los estados</option>
                                        <option value="available">Disponible</option>
                                        <option value="coming_soon">Próximamente</option>
                                        <option value="closed">Cerrado</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <span className="badge bg-light text-dark fs-6">
                                        {filteredCourses.length} resultado(s)
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de cursos mejorada */}
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Curso</th>
                                            <th>Empresa</th>
                                            <th>Categoría</th>
                                            <th>Modalidad</th>
                                            <th>Duración</th>
                                            <th>Precios</th>
                                            <th>Fechas</th>
                                            <th>Estado</th>
                                            <th>Solicitudes</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCourses.map(course => (
                                            <tr key={course.id}>
                                                <td>
                                                    <div>
                                                        <strong className="d-block">{course.title}</strong>
                                                        <small className="text-muted">{course.instructor}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <MdBusiness className="text-info me-1" />
                                                        <small>{course.company_name}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="badge bg-info">{course.category}</span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${
                                                        course.modality === 'Virtual' ? 'bg-primary' :
                                                        course.modality === 'Presencial' ? 'bg-warning' : 'bg-secondary'
                                                    }`}>
                                                        {course.modality}
                                                    </span>
                                                </td>
                                                <td>{course.duration}</td>
                                                <td>
                                                    <div>
                                                        {course.discount_percentage > 0 ? (
                                                            <>
                                                                <div className="text-decoration-line-through text-muted small">
                                                                    S/. {parseFloat(course.regular_price).toFixed(2)}
                                                                </div>
                                                                <div className="text-success fw-bold">
                                                                    S/. {parseFloat(course.discounted_price).toFixed(2)}
                                                                </div>
                                                                <span className="badge bg-danger small">
                                                                    <FaTag className="me-1" />
                                                                    -{course.discount_percentage}%
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <div className="text-success fw-bold">
                                                                S/. {parseFloat(course.regular_price).toFixed(2)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <small className="d-block">
                                                        <strong>Inicio:</strong> {new Date(course.start_date).toLocaleDateString()}
                                                    </small>
                                                    <small className="d-block text-muted">
                                                        <strong>Fin:</strong> {new Date(course.end_date).toLocaleDateString()}
                                                    </small>
                                                    {course.discount_deadline && course.discount_percentage > 0 && (
                                                        <small className="d-block text-warning">
                                                            <strong>Desc. hasta:</strong> {new Date(course.discount_deadline).toLocaleDateString()}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{getStatusBadge(course.status)}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-warning">{course.requestCount || 0}</span>
                                                </td>
                                                <td>
                                                    <div className="btn-group" role="group">
                                                        <button 
                                                            className="btn btn-sm btn-outline-info"
                                                            title="Ver solicitudes"
                                                            onClick={() => handleViewRequests(course)}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-warning"
                                                            onClick={() => handleEdit(course)}
                                                            title="Editar"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteClick(course)}
                                                            title="Eliminar"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Estado vacío */}
                            {filteredCourses.length === 0 && (
                                <div className="text-center py-5">
                                    <MdSchool size={64} className="text-muted mb-3" />
                                    <h4 className="text-muted">No hay cursos disponibles</h4>
                                    <p className="text-muted">
                                        {searchTerm || filterStatus !== 'all' || filterCategory !== 'all'
                                            ? 'No se encontraron cursos que coincidan con los filtros aplicados.'
                                            : 'Comienza creando tu primer curso.'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para crear/editar curso mejorado */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <MdSchool className="me-2" />
                                    {editingCourse ? 'Editar Curso' : 'Nuevo Curso'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    disabled={submitting}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        {/* Información básica */}
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">Título del Curso *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.title ? 'is-invalid' : ''}`}
                                                value={formData.title}
                                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                                placeholder="Ej: Gestión de Contratos Públicos"
                                                disabled={submitting}
                                            />
                                            {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Categoría *</label>
                                            <select
                                                className={`form-select ${formErrors.category ? 'is-invalid' : ''}`}
                                                value={formData.category}
                                                onChange={(e) => setFormData({...formData, category: e.target.value})}
                                                disabled={submitting}
                                            >
                                                <option value="">Seleccionar categoría...</option>
                                                {categories.map(category => (
                                                    <option key={category} value={category}>{category}</option>
                                                ))}
                                            </select>
                 
                                            {formErrors.category && <div className="invalid-feedback">{formErrors.category}</div>}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label">Descripción *</label>
                                            <textarea
                                                className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                                                rows="3"
                                                value={formData.description}
                                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                                placeholder="Describe el contenido y beneficios del curso..."
                                                disabled={submitting}
                                            ></textarea>
                                            {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                                        </div>

                                        {/* Empresa dictante */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">
                                                <MdBusiness className="me-2 text-info" />
                                                Empresa que dictará el curso *
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.company_name ? 'is-invalid' : ''}`}
                                                placeholder="Ej: Nuvia Consulting SAC"
                                                value={formData.company_name}
                                                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.company_name && <div className="invalid-feedback">{formErrors.company_name}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Instructor *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.instructor ? 'is-invalid' : ''}`}
                                                placeholder="Ej: Dr. Juan Pérez González"
                                                value={formData.instructor}
                                                onChange={(e) => setFormData({...formData, instructor: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.instructor && <div className="invalid-feedback">{formErrors.instructor}</div>}
                                        </div>

                                        {/* Precios y descuentos */}
                                        <div className="col-12 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <FaTag className="me-2 text-success" />
                                                        Configuración de Precios
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Precio Regular (S/.) *</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className={`form-control ${formErrors.regular_price ? 'is-invalid' : ''}`}
                                                                placeholder="450.00"
                                                                value={formData.regular_price}
                                                                onChange={(e) => setFormData({...formData, regular_price: e.target.value})}
                                                                disabled={submitting}
                                                            />
                                                            {formErrors.regular_price && <div className="invalid-feedback">{formErrors.regular_price}</div>}
                                                        </div>

                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Descuento (%)</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                className="form-control"
                                                                placeholder="20"
                                                                value={formData.discount_percentage}
                                                                onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})}
                                                                disabled={submitting}
                                                            />
                                                            <small className="form-text text-muted">Opcional: Porcentaje de descuento</small>
                                                        </div>

                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Precio con Descuento (S/.)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                value={formData.discounted_price}
                                                                disabled
                                                                style={{ backgroundColor: '#e9ecef' }}
                                                            />
                                                            <small className="form-text text-muted">Se calcula automáticamente</small>
                                                        </div>

                                                        {formData.discount_percentage > 0 && (
                                                            <div className="col-md-6 mb-3">
                                                                <label className="form-label">Fecha límite de descuento *</label>
                                                                <input
                                                                    type="date"
                                                                    className={`form-control ${formErrors.discount_deadline ? 'is-invalid' : ''}`}
                                                                    value={formData.discount_deadline}
                                                                    onChange={(e) => setFormData({...formData, discount_deadline: e.target.value})}
                                                                    disabled={submitting}
                                                                />
                                                                {formErrors.discount_deadline && <div className="invalid-feedback">{formErrors.discount_deadline}</div>}
                                                                <small className="form-text text-muted">Fecha hasta la cual estará vigente el descuento</small>
                                                            </div>
                                                        )}

                                                        {formData.regular_price && formData.discount_percentage > 0 && (
                                                            <div className="col-12">
                                                                <div className="alert alert-success">
                                                                    <strong>Vista previa:</strong> El cliente verá 
                                                                    <span className="text-decoration-line-through mx-2">S/. {formData.regular_price}</span>
                                                                    <span className="text-success fw-bold">S/. {formData.discounted_price}</span>
                                                                    <span className="badge bg-danger ms-2">-{formData.discount_percentage}% OFF</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detalles del curso */}
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Duración *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.duration ? 'is-invalid' : ''}`}
                                                placeholder="Ej: 40 horas"
                                                value={formData.duration}
                                                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.duration && <div className="invalid-feedback">{formErrors.duration}</div>}
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Modalidad *</label>
                                            <select
                                                className="form-select"
                                                value={formData.modality}
                                                onChange={(e) => setFormData({...formData, modality: e.target.value})}
                                                disabled={submitting}
                                            >
                                                <option value="Virtual">Virtual</option>
                                                <option value="Presencial">Presencial</option>
                                                <option value="Híbrida">Híbrida</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Máximo Estudiantes *</label>
                                            <input
                                                type="number"
                                                className={`form-control ${formErrors.max_students ? 'is-invalid' : ''}`}
                                                placeholder="30"
                                                value={formData.max_students}
                                                onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.max_students && <div className="invalid-feedback">{formErrors.max_students}</div>}
                                        </div>

                                        {/* Fechas */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Fecha de Inicio *</label>
                                            <input
                                                type="date"
                                                className={`form-control ${formErrors.start_date ? 'is-invalid' : ''}`}
                                                value={formData.start_date}
                                                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.start_date && <div className="invalid-feedback">{formErrors.start_date}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Fecha de Fin *</label>
                                            <input
                                                type="date"
                                                className={`form-control ${formErrors.end_date ? 'is-invalid' : ''}`}
                                                value={formData.end_date}
                                                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                                disabled={submitting}
                                            />
                                            {formErrors.end_date && <div className="invalid-feedback">{formErrors.end_date}</div>}
                                        </div>

                                        {/* Información adicional */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Requisitos</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={formData.requirements}
                                                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                                                placeholder="Ej: Conocimientos básicos en contratación pública, título universitario..."
                                                disabled={submitting}
                                            ></textarea>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label">Objetivos del Curso</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={formData.objectives}
                                                onChange={(e) => setFormData({...formData, objectives: e.target.value})}
                                                placeholder="Ej: Al finalizar el curso, el participante será capaz de..."
                                                disabled={submitting}
                                            ></textarea>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label">Temario/Syllabus</label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                value={formData.syllabus}
                                                onChange={(e) => setFormData({...formData, syllabus: e.target.value})}
                                                placeholder="Módulo 1: Introducción&#10;Módulo 2: Desarrollo&#10;Módulo 3: Aplicación práctica..."
                                                disabled={submitting}
                                            ></textarea>
                                        </div>

                                        {/* Configuración adicional */}
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">URL de Imagen</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                placeholder="https://ejemplo.com/imagen-curso.jpg"
                                                value={formData.image_url}
                                                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                                                disabled={submitting}
                                            />
                                            <small className="form-text text-muted">URL de la imagen promocional del curso</small>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Estado *</label>
                                            <select
                                                className="form-select"
                                                value={formData.status}
                                                onChange={(e) => setFormData({...formData, status: e.target.value})}
                                                disabled={submitting}
                                            >
                                                <option value="available">Disponible</option>
                                                <option value="coming_soon">Próximamente</option>
                                                <option value="closed">Cerrado</option>
                                            </select>
                                        </div>

                                        {/* Estudiantes actuales - solo mostrar si está editando */}
                                        {editingCourse && (
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">Estudiantes Actuales</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={formData.current_students}
                                                    onChange={(e) => setFormData({...formData, current_students: parseInt(e.target.value) || 0})}
                                                    disabled={submitting}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                        disabled={submitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <MdSchool className="me-2" />
                                                {editingCourse ? 'Actualizar' : 'Crear'} Curso
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de solicitudes de información */}
            {showRequestsModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
<div className="modal-header">
    <h5 className="modal-title">
        <FaUsers className="me-2 text-info" />
        Solicitudes de Información
    </h5>
    <div>
        <button 
            type="button" 
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => handleViewRequests(selectedCourseRequests.course)}
            disabled={loadingRequests}
        >
            <FaDownload className="me-1" />
            Actualizar
        </button>
        <button 
            type="button"
            className="btn-close"
            onClick={() => setShowRequestsModal(false)}
        ></button>
    </div>
</div>
                            <div className="modal-body">
                                {loadingRequests ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary mb-3" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                        <p>Cargando solicitudes...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="alert alert-info">
                                            <strong>Curso:</strong> {selectedCourseRequests.course?.title}
                                        </div>
                                        
                                        {selectedCourseRequests.requests?.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Nombre</th>
                                                            <th>Teléfono</th>
                                                            <th>Email</th>
                                                            <th>Fecha</th>
                                                            <th>Estado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedCourseRequests.requests.map(request => (
                                                            <tr key={request.id}>
                                                                <td>
                                                                    <strong>{request.name}</strong>
                                                                    {request.company && request.company !== 'No especificada' && (
                                                                 <small className="d-block text-muted">{request.company}</small>
                                                              )}
                        </td>
                        <td>
                            <a href={`mailto:${request.email}`} className="text-decoration-none">
                                {request.email}
                            </a>
                        </td>
                        <td>
                            <a href={`tel:${request.phone}`} className="text-decoration-none">
                                {request.phone}
                            </a>
                        </td>
                        <td>
                            <small>{new Date(request.request_date).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}</small>
                        </td>
<td>
    <select
        className={`form-select form-select-sm ${
            request.status === 'pending' ? 'text-warning' :
            request.status === 'contacted' ? 'text-success' : 
            request.status === 'enrolled' ? 'text-primary' :
            request.status === 'cancelled' ? 'text-danger' : 'text-secondary'
        }`}
        value={request.status}
        onChange={(e) => updateRequestStatus(request.id, e.target.value)}
        style={{ 
            minWidth: '130px',
            fontSize: '0.875rem',
            fontWeight: '600'
        }}
    >
        <option value="pending">⏳ Pendiente</option>
        <option value="contacted">📞 Contactado</option>
        <option value="enrolled">✅ Matriculado</option>
        <option value="cancelled">❌ Cancelado</option>
        <option value="completed">🎓 Completado</option>
    </select>
</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <FaUsers size={48} className="text-muted mb-3" />
                                                <h5 className="text-muted">No hay solicitudes</h5>
                                                <p className="text-muted">Aún no se han recibido solicitudes para este curso.</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowRequestsModal(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de eliminación */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">
                                    <FaTrash className="me-2" />
                                    Confirmar Eliminación
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setCourseToDelete(null);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <strong>¡Atención!</strong> Esta acción no se puede deshacer.
                                </div>
                                <p>
                                    ¿Estás seguro de que deseas eliminar el curso <strong>"{courseToDelete?.title}"</strong>?
                                </p>
                                {courseToDelete?.current_students > 0 && (
                                    <div className="alert alert-danger">
                                        <strong>Advertencia:</strong> Este curso tiene {courseToDelete.current_students} estudiante(s) registrado(s).
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setCourseToDelete(null);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    <FaTrash className="me-2" />
                                    Eliminar Curso
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseAdmin;